import * as React from 'react';
import {useMemo} from 'react';
import {useSearchParams} from "react-router-dom";
import {FtDate} from "@/common/util/FtDate";
import {YMInput} from "@/component/input/YMInput";
import {YmNav} from "@/component/input/YmNav";
import {LoadableView} from "@/component/view/LoadableView";
import {useAccountStore} from "@/store/useAccountStore";
import {useDialog} from "@/dialog/DialogContext";
import {FtGrid, FtGridCol} from "@/component/layout/FtGrid";
import {TransactionListView, TransactionListViewItem} from "@/component/view/TransactionListView";
import {FtButton} from "@/common/FtButton";
import {TransAccount} from "@/common/dto/TransAccount";
import {useTransLimits, useTransLimitsSummary} from "@/hook/useTransLimits";
import {useTransactionAccountSummary, useTransactions} from "@/hook/useTransactions";

export function TransLimitsPage() {
    const allAccounts = useAccountStore(state => state.accounts);
    const [searchParams, setSearchParams] = useSearchParams();

    const month = searchParams.get("month") ?? new FtDate().ym("-");
    const ftMonth = new FtDate(month);
    const prevMonthDate = ftMonth.addMonth(-1).lastDayOfMonth().ymd("-");
    const thisMonthBegin = ftMonth.setDate(1).ymd();
    const thisMonthEnd = ftMonth.lastDayOfMonth().ymd("-");

    const accounts = useMemo(() => allAccounts.filter(a => !a.archived && a.useLimit).sort((a, b) => a.name.localeCompare(b.name))
        , [allAccounts]);

    return <div>
        <div style={{display: "flex", gap: 16, marginBottom: 16}}>
            <YMInput value={month} onChange={(v) => setSearchParams({month: v})}/>
            <YmNav value={month} onChange={(v) => setSearchParams({month: v})}/>
        </div>

        <FtGrid>
            {accounts.map(account => <AccountView
                key={account.id}
                account={account}
                prevMonthDate={prevMonthDate}
                thisMonthBegin={thisMonthBegin} thisMonthEnd={thisMonthEnd}
            />)}
        </FtGrid>
    </div>;
}

function AccountView(props: {
    account: TransAccount,
    prevMonthDate: string,
    thisMonthBegin: string,
    thisMonthEnd: string
}) {
    const {account, prevMonthDate, thisMonthBegin, thisMonthEnd} = props;

    const {data: limitSums, error: error1} = useTransLimitsSummary(account.id, undefined, prevMonthDate);
    const {data: transSum, error: error2} = useTransactionAccountSummary(account.id, undefined, prevMonthDate);
    const {data: transLimits, error: error3} = useTransLimits(account.id, thisMonthBegin, thisMonthEnd);
    const {data: transactions, error: error4} = useTransactions({
        accountId: account.id,
        transactDateFrom: thisMonthBegin,
        transactDateTo: thisMonthEnd,
    });
    const dialog = useDialog();

    const prevBalance = useMemo<number | undefined>(() => {
        if (!limitSums || !transSum) return undefined;
        const limitSum = limitSums.find(l => l.accountId === account.id) ?? {amount: 0};
        return limitSum.amount + transSum.outSum - transSum.inSum;
    }, [limitSums, transSum]);

    const items: TransactionListViewItem[] | undefined = useMemo(() => {
        if (!transLimits || !transactions) return undefined;

        const items: TransactionListViewItem[] = [];

        for (const limit of transLimits) {
            if (limit.accountId !== account.id) continue;
            items.push({
                key: `limit-${limit.id}`,
                date: limit.applyDate,
                type: '한도 부여',
                inAmount: limit.amount,
                outAmount: 0,
                detail: limit.detail,
                onClick: () => dialog.openDialog('editTransLimit', {transLimitId: limit.id}),
            });
        }

        for (const transaction of transactions) {
            items.push({
                key: `trans-${transaction.id}`,
                date: transaction.transactDate,
                type: '지출',
                outAmount:(transaction.inAccountId === account.id) ? transaction.amount : -transaction.amount,
                inAmount: 0,
                detail: transaction.detail,
                onClick: () => dialog.openDialog('editTrans', {transId: transaction.id}),
            });
        }

        items.sort((a, b) => {
            return a.date.localeCompare(b.date);
        });

        return items;
    }, [transLimits, transactions, account, dialog]);

    return <FtGridCol>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <h3>{account.name}</h3>
            <FtButton
                variant="outlined"
                onClick={() => dialog.openDialog('addTransLimit', {accountId: account.id, applyDate: thisMonthBegin})}
            >한도 추가</FtButton>
        </div>

        <LoadableView data={items} error={error1 || error2 || error3 || error4} component={items => (
            <TransactionListView prevBalance={prevBalance} items={items} splitInOut={true} />
        )}/>

    </FtGridCol>;
}